'use client';

import { useState, useEffect } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FilterX, X } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { useScraperStore } from '@/stores/useScraperStore';

interface ScraperFilterSidebarProps {
  className?: string;
  onMobileClose?: () => void;
  isMobile?: boolean;
}

// Source platforms options
const sourcePlatforms = [
  { id: 'linkedin', label: 'LinkedIn (Sales Nav, Regular)' },
  { id: 'apollo', label: 'Apollo.io' },
  { id: 'crunchbase', label: 'Crunchbase' },
  { id: 'facebook', label: 'Facebook / Instagram' },
  { id: 'industry', label: 'Specific Industry Sites (e.g., Zillow, G2)' },
  { id: 'custom', label: 'Custom Website Scraping' },
  { id: 'other', label: 'Other' },
];

// Service type options
const serviceTypes = [
  { id: 'lead_generation', label: 'Lead Generation' },
  { id: 'company_data', label: 'Company Data Extraction' },
  { id: 'contact_finding', label: 'Contact Finding/Verification' },
  { id: 'market_research', label: 'Market Research Data' },
];

// Delivery time options
const deliveryTimeOptions = [
  { id: '<24h', label: '< 24 Hours' },
  { id: '1-2 days', label: '1-2 Days' },
  { id: '2-3 days', label: '2-3 Days' },
  { id: '3+', label: '3+ Days' },
];

export default function ScraperFilterSidebar({
  className,
  onMobileClose,
  isMobile = false,
}: ScraperFilterSidebarProps) {
  // Get values and actions from Zustand store
  const {
    sourcePlatforms: selectedPlatforms,
    serviceType,
    priceRange,
    deliveryTime,
    providerRating,
    setSourcePlatforms,
    setServiceType,
    setPriceRange,
    setDeliveryTime,
    setProviderRating,
    resetFilters: resetAllFilters,
  } = useScraperStore();

  // Local UI state for form inputs
  const [localPlatforms, setLocalPlatforms] = useState<string[]>(selectedPlatforms);
  const [localServiceType, setLocalServiceType] = useState<string | undefined>(serviceType);
  const [localPriceRange, setLocalPriceRange] = useState<{ min: string; max: string }>({
    min: priceRange.min?.toString() || '',
    max: priceRange.max?.toString() || '',
  });
  const [localDeliveryTime, setLocalDeliveryTime] = useState<string | undefined>(deliveryTime);
  const [localProviderRating, setLocalProviderRating] = useState<number | undefined>(
    providerRating,
  );

  // Update local state when store changes
  useEffect(() => {
    setLocalPlatforms(selectedPlatforms);
    setLocalServiceType(serviceType);
    setLocalPriceRange({
      min: priceRange.min?.toString() || '',
      max: priceRange.max?.toString() || '',
    });
    setLocalDeliveryTime(deliveryTime);
    setLocalProviderRating(providerRating);
  }, [selectedPlatforms, serviceType, priceRange, deliveryTime, providerRating]);

  // Apply filters to store
  const applyFilters = () => {
    // Update price range
    setPriceRange({
      min: localPriceRange.min ? parseInt(localPriceRange.min) : undefined,
      max: localPriceRange.max ? parseInt(localPriceRange.max) : undefined,
    });

    // Close mobile sidebar if on mobile
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  // Apply a specific filter change immediately
  const applyFilterChange = (filterChange: () => void) => {
    filterChange();
    applyFilters();
  };

  // Handle platform change
  const handlePlatformChange = (platformId: string, checked: boolean) => {
    const newPlatforms = checked
      ? [...localPlatforms, platformId]
      : localPlatforms.filter((id) => id !== platformId);

    setLocalPlatforms(newPlatforms);
    applyFilterChange(() => setSourcePlatforms(newPlatforms));
  };

  // Reset all filters
  const resetFilters = () => {
    // Reset local state
    setLocalPlatforms([]);
    setLocalServiceType(undefined);
    setLocalPriceRange({ min: '', max: '' });
    setLocalDeliveryTime(undefined);
    setLocalProviderRating(undefined);

    // Reset store state
    resetAllFilters();

    // Close mobile sidebar if on mobile
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return (
      localPlatforms.length > 0 ||
      localServiceType !== undefined ||
      localPriceRange.min !== '' ||
      localPriceRange.max !== '' ||
      localDeliveryTime !== undefined ||
      localProviderRating !== undefined
    );
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Source Platforms */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Source Platforms</h3>
          {localPlatforms.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => applyFilterChange(() => setSourcePlatforms([]))}
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
        <div className="space-y-2.5">
          {sourcePlatforms.map((platform) => (
            <div key={platform.id} className="flex items-center space-x-2">
              <Checkbox
                id={`platform-${platform.id}`}
                checked={localPlatforms.includes(platform.id)}
                onCheckedChange={(checked) => handlePlatformChange(platform.id, checked as boolean)}
              />
              <Label htmlFor={`platform-${platform.id}`} className="text-sm cursor-pointer">
                {platform.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Service Type */}
      <div className="space-y-3 border-t border-border pt-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Service Type</h3>
          {localServiceType && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => applyFilterChange(() => setServiceType(undefined))}
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
        <RadioGroup
          value={localServiceType || ''}
          onValueChange={(value) => {
            setLocalServiceType(value || undefined);
            applyFilterChange(() => setServiceType(value || undefined));
          }}
          className="space-y-2.5"
        >
          {serviceTypes.map((type) => (
            <div key={type.id} className="flex items-center space-x-2">
              <RadioGroupItem value={type.id} id={`service-${type.id}`} />
              <Label htmlFor={`service-${type.id}`} className="text-sm cursor-pointer">
                {type.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Price Range */}
      <div className="space-y-3 border-t border-border pt-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Price Range (£)</h3>
          {(localPriceRange.min !== '' || localPriceRange.max !== '') && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => {
                setLocalPriceRange({ min: '', max: '' });
                applyFilterChange(() => setPriceRange({ min: undefined, max: undefined }));
              }}
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label htmlFor="min-price" className="text-xs">
              Min
            </Label>
            <Input
              id="min-price"
              type="number"
              placeholder="0"
              min="0"
              value={localPriceRange.min}
              onChange={(e) => setLocalPriceRange((prev) => ({ ...prev, min: e.target.value }))}
              onBlur={applyFilters}
              className="h-8"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="max-price" className="text-xs">
              Max
            </Label>
            <Input
              id="max-price"
              type="number"
              placeholder="Max"
              min="0"
              value={localPriceRange.max}
              onChange={(e) => setLocalPriceRange((prev) => ({ ...prev, max: e.target.value }))}
              onBlur={applyFilters}
              className="h-8"
            />
          </div>
        </div>
      </div>

      {/* Delivery Time */}
      <div className="space-y-3 border-t border-border pt-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Delivery Time</h3>
          {localDeliveryTime && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => applyFilterChange(() => setDeliveryTime(undefined))}
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
        <RadioGroup
          value={localDeliveryTime || ''}
          onValueChange={(value) => {
            setLocalDeliveryTime(value || undefined);
            applyFilterChange(() => setDeliveryTime(value || undefined));
          }}
          className="space-y-2.5"
        >
          {deliveryTimeOptions.map((option) => (
            <div key={option.id} className="flex items-center space-x-2">
              <RadioGroupItem value={option.id} id={`delivery-${option.id}`} />
              <Label htmlFor={`delivery-${option.id}`} className="text-sm cursor-pointer">
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Action Buttons */}
      {hasActiveFilters() && (
        <div className="flex flex-col space-y-2 pt-2 border-t border-border">
          <Button
            variant="outline"
            onClick={resetFilters}
            size={isMobile ? 'default' : 'sm'}
            className="flex items-center justify-center gap-1.5 w-full"
          >
            <FilterX className="h-4 w-4" />
            Reset All Filters
          </Button>
        </div>
      )}
    </div>
  );
}
